/**
 * NavButton - Individual navigation button component
 *
 * Renders navigation buttons that adapt between collapsed (icon-only) and expanded states.
 * Handles both regular navigation items and section headers with collapsible children.
 */

import { ActionIcon, Avatar, Button, Group, Text, Tooltip } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { Link, useNavigate } from "@tanstack/react-router";
import { NavItem } from "./NavigationItems";
import { NavSectionPopover } from "./NavSectionPopover";

interface NavButtonProps {
  /** The navigation item to render */
  item: NavItem;
  /** Whether this is a child item in a collapsible section */
  isChild?: boolean;
  /** Whether to render in collapsed (icon-only) mode */
  collapsed?: boolean;
  /** Whether this item or any of its children are currently active */
  isActive: boolean;
  /** Whether the collapsible section is currently expanded */
  isExpanded?: boolean;
  /** Callback to toggle section expansion */
  onToggleSection?: () => void;
  /** Function to get active state for child items (used by popover) */
  getChildActiveState?: (child: NavItem) => boolean;
  /** Callback when a navigation link is clicked (used to close mobile nav) */
  onNavigate?: () => void;
}

export const NavButton = ({
                            item,
                            isChild = false,
                            collapsed = false,
                            isActive,
                            isExpanded = false,
                            onToggleSection,
                            getChildActiveState,
                            onNavigate
                          }: NavButtonProps) => {
  const navigate = useNavigate();

  // Handle section click - navigate to first child and expand if collapsed
  const handleSectionClick = () => {
    if (item.children?.[0]?.path) {
      navigate({to: item.children[0].path});
      onNavigate?.();
    }
    if (collapsed || !isExpanded) {
      onToggleSection?.();
    }
  };

  // Handle direct link click (non-section items use Link component, so we just need to close mobile)
  const handleLinkClick = () => {
    onNavigate?.();
  };

  // Collapsed view: Show only icon with tooltip (or popover for sections with children)
  if (collapsed) {
    const button = (
      <Button
        component={item.path && !item.children ? Link : undefined}
        to={item.path && !item.children ? item.path : undefined}
        onClick={item.children ? handleSectionClick : handleLinkClick}
        variant="subtle"
        size="lg"
        aria-label={item.label}
      >
        <Avatar size="sm" color={isActive ? "blue" : "gray"} variant={isActive ? "filled" : "light"}>
          {item.icon}
        </Avatar>
      </Button>
    );

    // Show popover for sections with children, tooltip for regular items
    if (item.children && getChildActiveState) {
      return (
        <NavSectionPopover
          key={item.label}
          item={item}
          getChildActiveState={getChildActiveState}
        >
          {button}
        </NavSectionPopover>
      );
    }

    return (
      <Tooltip key={item.label} label={item.label} position="right" withArrow>
        {button}
      </Tooltip>
    );
  }

  // Expanded view: Show full button with icon, label, and optional chevron
  return (
    <Button
      key={item.label}
      component={item.path && !item.children ? Link : undefined}
      to={item.path && !item.children ? item.path : undefined}
      onClick={item.children ? handleSectionClick : handleLinkClick}
      variant={isActive ? "light" : "subtle"}
      color={isActive ? "blue" : "var(--mantine-color-default-color)"}
      justify="start"
      fullWidth={isChild}
      rightSection={item.children ? (
        <ActionIcon
          component="div"
          variant="subtle"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleSection?.();
          }}
        >
          {isExpanded ? <IconChevronDown size={16}/> : <IconChevronRight size={16}/>}
        </ActionIcon>
      ) : undefined}
    >
      <Group gap="sm" wrap="nowrap">
        <Avatar size="sm" radius="xl" color={isActive ? "blue" : "gray"} variant={isActive ? "filled" : "light"}>
          {item.icon}
        </Avatar>
        <Text fw={isActive ? 600 : 400} c={isActive ? "blue" : undefined}>
          {item.label}
        </Text>
      </Group>
    </Button>
  );
};